/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/* Import libraries */
import { DeviceSensor } from '@/lib/sensors/catalog/deviceSensor';
import { DeviceSensorCatalog } from '@/lib/sensors/catalog/deviceSensorCatalog';
import { MonitoringUrl } from '@/lib/sensors/urls/monitoringUrl';

/* Import types */
import type { DeviceSensorGroup, ResolvedDeviceNames } from '@/types/deviceSensor';

const LEGACY_BASE_SENSOR_TYPES = ['P', 'P_1d', 'P_15m'];
const SENSOR_GROUPS_WITH_OPTIONAL_BASE_ENTRY: DeviceSensorGroup[] = ['DTC', 'BL', 'HE', 'PV', 'WL'];

interface ReverseResolvedSensorData extends ResolvedDeviceNames {
    description: string | null;
    sensorGroup: DeviceSensorGroup;
    sensorName: string;
    sensorType: string | null;
    sensorUrl: string;
}

export class ReverseDeviceSensor {
    private static readonly sensorLookups = new WeakMap<
        DeviceSensorCatalog,
        Map<string, ReverseResolvedSensorData>
    >();

    private readonly requestedSensorName: string;
    private readonly resolvedSensorData: ReverseResolvedSensorData | null;

    constructor(
        sensorName: string,
        catalog: DeviceSensorCatalog = new DeviceSensorCatalog(),
    ) {
        this.requestedSensorName = sensorName;
        this.resolvedSensorData = ReverseDeviceSensor.resolveSensorData(sensorName, catalog);
    }

    static resolveSensorName(
        sensorName: string,
        catalog: DeviceSensorCatalog = new DeviceSensorCatalog(),
    ): ResolvedDeviceNames | null {
        const resolvedSensorData = ReverseDeviceSensor.resolveSensorData(sensorName, catalog);

        if (!resolvedSensorData) {
            return null;
        }

        return {
            realDeviceName: resolvedSensorData.realDeviceName,
            virtualDeviceName: resolvedSensorData.virtualDeviceName,
        };
    }

    getRealDeviceName(): string | null {
        return this.resolvedSensorData?.realDeviceName ?? null;
    }

    getSensorGroup(): string | null {
        return this.resolvedSensorData?.sensorGroup ?? null;
    }

    getDeviceDescription(): string | null {
        return this.resolvedSensorData?.description ?? null;
    }

    getSensorName(): string | null {
        return this.resolvedSensorData?.sensorName ?? null;
    }

    getSensorType(fallbackToLastSegment = false): string | null {
        if (this.resolvedSensorData?.sensorType) {
            return this.resolvedSensorData.sensorType;
        }

        if (!fallbackToLastSegment) {
            return null;
        }

        const sensorNameParts = (this.resolvedSensorData?.sensorName ?? this.requestedSensorName).split('.');

        return sensorNameParts[sensorNameParts.length - 1] ?? null;
    }

    getSensorUrl(): string | null {
        return this.resolvedSensorData?.sensorUrl ?? null;
    }

    getVirtualDeviceName(): string | null {
        return this.resolvedSensorData?.virtualDeviceName ?? null;
    }

    private static addSensorToLookup(
        catalog: DeviceSensorCatalog,
        sensorLookup: Map<string, ReverseResolvedSensorData>,
        sensorName: string | null,
        sensorGroup: DeviceSensorGroup,
        sensorType: string | null,
        resolvedDeviceNames: ResolvedDeviceNames,
        sensorUrl: string | null,
    ): void {
        if (!sensorName || !sensorUrl) {
            return;
        }

        sensorLookup.set(sensorName, {
            ...resolvedDeviceNames,
            description: catalog.getDeviceDescription(resolvedDeviceNames.virtualDeviceName),
            sensorGroup,
            sensorName,
            sensorType,
            sensorUrl,
        });
    }

    private static resolveSensorData(
        sensorName: string,
        catalog: DeviceSensorCatalog = new DeviceSensorCatalog(),
    ): ReverseResolvedSensorData | null {
        const sensorLookup = ReverseDeviceSensor.buildSensorLookup(catalog);

        return sensorLookup.get(sensorName) ?? null;
    }

    private static buildSensorLookup(
        catalog: DeviceSensorCatalog,
    ): Map<string, ReverseResolvedSensorData> {
        const cachedLookup = ReverseDeviceSensor.sensorLookups.get(catalog);

        if (cachedLookup) {
            return cachedLookup;
        }

        const sensorLookup = new Map<string, ReverseResolvedSensorData>();

        for (const virtualDeviceName of catalog.getVirtualDevices()) {
            const resolvedDeviceNames = catalog.resolveDeviceNames(virtualDeviceName);
            const deviceSensor = new DeviceSensor(virtualDeviceName, catalog);

            ReverseDeviceSensor.addSensorToLookup(
                catalog,
                sensorLookup,
                resolvedDeviceNames.virtualDeviceName,
                null,
                null,
                resolvedDeviceNames,
                new MonitoringUrl(resolvedDeviceNames.virtualDeviceName).getUrl(),
            );
            ReverseDeviceSensor.addSensorToLookup(
                catalog,
                sensorLookup,
                resolvedDeviceNames.realDeviceName,
                null,
                null,
                resolvedDeviceNames,
                new MonitoringUrl(resolvedDeviceNames.realDeviceName).getUrl(),
            );

            const baseSensorTypes = new Set([
                ...LEGACY_BASE_SENSOR_TYPES,
                ...catalog.getSensorTypes(null),
                ...deviceSensor.getSensorTypes(null),
            ]);

            for (const sensorType of baseSensorTypes) {
                const sensorName = deviceSensor.getSensorName(null, sensorType);

                if (sensorName === resolvedDeviceNames.virtualDeviceName) {
                    continue;
                }

                ReverseDeviceSensor.addSensorToLookup(
                    catalog,
                    sensorLookup,
                    sensorName,
                    null,
                    sensorType,
                    resolvedDeviceNames,
                    deviceSensor.getSensorUrl(null, sensorType),
                );
            }

            for (const group of catalog.getSensorGroups(virtualDeviceName)) {
                if (!SENSOR_GROUPS_WITH_OPTIONAL_BASE_ENTRY.includes(group)) {
                    continue;
                }

                ReverseDeviceSensor.addSensorToLookup(
                    catalog,
                    sensorLookup,
                    deviceSensor.getSensorName(group, null),
                    group,
                    null,
                    resolvedDeviceNames,
                    deviceSensor.getSensorUrl(group, null),
                );

                for (const sensorType of catalog.getSensorTypes(group)) {
                    ReverseDeviceSensor.addSensorToLookup(
                        catalog,
                        sensorLookup,
                        deviceSensor.getSensorName(group, sensorType),
                        group,
                        sensorType,
                        resolvedDeviceNames,
                        deviceSensor.getSensorUrl(group, sensorType),
                    );
                }
            }
        }

        ReverseDeviceSensor.sensorLookups.set(catalog, sensorLookup);

        return sensorLookup;
    }
}
